/**
 * 属性の機械生成の起動（オペレーター専用）。
 *
 * ## 押せない理由を画面に書く
 *
 * 「パイプラインを起動」は、1 段目（商品画像の取り込み）が成功しているときだけ
 * 押せる。押せないときは**その理由をボタンのそばに出す**。無効なボタンだけを
 * 置くと、押せないことは分かっても理由が分からない。
 *
 * ## 設定が未投入なら、押す前に分かる形にする
 *
 * 起動の口は、Databricks のジョブ ID / パイプライン ID が未投入なら何も
 * 起こさずに断る。以前はそれが**押してから**分かる形で、押せる見た目の
 * ボタンを押すと「まだ有効になっていません」と返ってきた。
 * `productSemanticsFillAvailability` が段ごとの真偽を返すので、使えない段は
 * ボタンを無効にして断り書きを出す。**1 段目と 2 段目は別の設定**なので、
 * 真偽も文面も分ける。
 *
 * 返るのは**有無だけ**で、ジョブ ID やトークンの値は画面に来ない。
 * 判断の正本はサーバー側（起動口）のままで、ここは先に見せるだけである。
 * 読めなかったときは**使えない側へ倒さない** — 押せる状態のままにし、
 * 断るのは起動のほうに任せる（読めないことを「未設定」と決めつけない）。
 *
 * ## 状態が読めないときに「成功」と書かない
 *
 * 状態は台帳ではなく Databricks から読む。読めなければ `UNKNOWN` が返り、
 * ここでは「状態を確認できません」と出す。2 段目まで進んだ行は、更新の状態を
 * 読む口がまだ無いので必ずこうなる。
 *
 * ## 「終わったか」と「何件取り込めたか」を分けて出す
 *
 * 取り込みは**対象 30 件・取得 0 件・失敗 30 件でも成功で終わる**。札に
 * 「成功しました」とだけ出すと、1 枚も取り込めていない実行が成功に見える。
 * 札はジョブの終わり方だけを出し、件数は別の欄に出す。件数が読めなければ
 * 「件数は読めません」で、**0 件とは書かない**。
 *
 * ## 件数の上限は API と同じ 30
 *
 * 画面から上限を超える値を送らない。上限そのものはサーバー側の定数が正本で、
 * ここはそれに合わせているだけである。
 */

import { useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Tag,
  formatJstDateTime,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/index";
import { parseProductIds } from "./product-ids-input";
import {
  OperatorProductSemanticsFillAvailabilityDocument,
  OperatorProductSemanticsFillsDocument,
  OperatorStartProductSemanticsFillDocument,
  OperatorStartProductSemanticsPipelineDocument,
  OperatorTenantSummariesDocument,
  type ProductSemanticsFillStage,
  type ProductSemanticsFillState,
} from "../graphql/generated/graphql";

/** 1 回に取り込む件数。API の上限（30）を超える値を画面から送らない。 */
const LIMIT_MIN = 1;
const LIMIT_MAX = 30;
const LIMIT_DEFAULT = 30;

/** 1 ページに並べる起動の数。API の上限（25）を超える値を送らない。 */
const PAGE_SIZE = 20;

/** 会社の選択に並べる数。比較の画面と同じ上限。 */
const TENANT_PAGE_SIZE = 20;

const STATE_UNKNOWN_LABEL = "状態を確認できません";

/** 件数が読めなかった行の文面。**0 件と書かない。** */
const COUNTS_UNREADABLE_LABEL = "件数は読めません";

/**
 * 取り込めた画像が 1 枚も無いときの文面。
 *
 * 判断の正本はサーバー側（断るのは API）で、ここはその理由を先に見せる
 * ためのものである。画面だけで止めない。
 */
const NO_IMAGES_REASON =
  "取り込めた画像が 0 件のため、属性の生成を始めても何も作られません。";

/**
 * 設定が未投入のときの断り書き。**段ごとに分ける。**
 *
 * 1 段目と 2 段目は別の設定なので、片方だけ入っている状態が実際にありうる。
 * まとめて「属性の機械生成は使えません」と書くと、どちらを入れればよいのかが
 * 読んだ人に分からない。文面はサーバー側の断り（`userErrors`）と揃えてある。
 */
const IMAGE_FETCH_UNCONFIGURED_REASON =
  "商品画像の取り込みは、この環境ではまだ有効になっていません。設定が済むまでお待ちください。";

const PIPELINE_UNCONFIGURED_REASON =
  "属性の生成（パイプライン）は、この環境ではまだ有効になっていません。設定が済むまでお待ちください。";

/**
 * ジョブの**終わり方**の呼び名。件数の結果はここに混ぜない。
 *
 * 「成功しました」と書くと、1 枚も取り込めていない実行まで成功に見える。
 * ここは終わり方だけを出す。
 */
function stateLabel(state: ProductSemanticsFillState): string {
  switch (state) {
    case "RUNNING":
      return "動いています";
    case "SUCCEEDED":
      return "ジョブは正常に終了";
    case "FAILED":
      return "ジョブは失敗しました";
    default:
      // **「成功」と書かない。** 読めないことをそのまま出す
      return STATE_UNKNOWN_LABEL;
  }
}

/**
 * 失敗の理由の呼び名（`etl/pipelines/lib/product_image_fetch.py` の定数）。
 *
 * 一覧に無い理由は**そのまま出す**。知らない理由を「その他」に丸めると、
 * 何が起きたのか画面から分からなくなる。
 */
const FAILURE_REASON_LABELS: Readonly<Record<string, string>> = {
  url_rejected: "画像の URL が許可されていない宛先",
  redirect_rejected: "転送先が許可されていない宛先",
  http_error: "取得先がエラーを返した",
  non_image_mime: "画像ではない種別が返った",
  empty_body: "中身が空だった",
  timeout: "時間内に応答が無かった",
  url_error: "URL へ届かなかった",
  unsafe_product_id: "商品 ID の形が扱えない",
};

function failureReasonLabel(reason: string): string {
  const label = FAILURE_REASON_LABELS[reason];
  return label === undefined ? reason : `${label}（${reason}）`;
}

/** 件数の結果。読めなければ「読めません」と出す（0 件と書かない）。 */
function countsLabel(
  counts: { fetched: number; skippedExisting: number; failed: number } | null,
  state: ProductSemanticsFillState,
): string {
  if (counts === null) {
    return state === "RUNNING"
      ? "まだ終わっていません"
      : COUNTS_UNREADABLE_LABEL;
  }
  return `取得 ${counts.fetched} 件 / 既にあり ${counts.skippedExisting} 件 / 失敗 ${counts.failed} 件`;
}

function stageLabel(stage: ProductSemanticsFillStage): string {
  return stage === "SEMANTICS_PIPELINE"
    ? "パイプラインまで起動済み"
    : "商品画像の取り込み";
}

/**
 * 2 段目を起こせない理由。押せるときは `null`。
 *
 * **設定の未投入を先に見る。** 行の状態がどうであれ、2 段目の設定が入って
 * いなければ起こせない。設定が読めなかったとき（`pipelineEnabled` が
 * `undefined`）は塞がない — 読めないことを「未設定」と決めつけない。
 */
function pipelineBlockedReason(
  fill: {
    stage: ProductSemanticsFillStage;
    state: ProductSemanticsFillState;
    counts: { fetched: number; skippedExisting: number } | null;
  },
  pipelineEnabled?: boolean,
): string | null {
  if (pipelineEnabled === false) return PIPELINE_UNCONFIGURED_REASON;
  if (fill.stage === "SEMANTICS_PIPELINE") {
    return "パイプラインはすでに起動しています。";
  }
  switch (fill.state) {
    case "SUCCEEDED":
      // **成功は「終わった」であって「取り込めた」ではない。**
      // 件数が読めないときは止めない（読めないことを 0 件と決めつけない）
      if (
        fill.counts !== null &&
        fill.counts.fetched + fill.counts.skippedExisting === 0
      ) {
        return NO_IMAGES_REASON;
      }
      return null;
    case "RUNNING":
      return "商品画像の取り込みがまだ動いています。終わるまで待ってください。";
    case "FAILED":
      return "商品画像の取り込みが失敗しました。もう一度取り込みから始めてください。";
    default:
      return "商品画像の取り込みの状態を確認できないため、起動できません。";
  }
}

export function SemanticsFillPage() {
  const [tenantId, setTenantId] = useState("");
  const [limit, setLimit] = useState(LIMIT_DEFAULT);
  const [productIdsText, setProductIdsText] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  const [tenants] = useQuery({
    query: OperatorTenantSummariesDocument,
    variables: { first: TENANT_PAGE_SIZE },
  });

  // 環境の設定の話なので、会社を選ぶ前から引ける。
  // 読めなかったときは `undefined` のままにして、**塞がない**
  const [availability] = useQuery({
    query: OperatorProductSemanticsFillAvailabilityDocument,
  });
  const imageFetchEnabled =
    availability.data?.operator.productSemanticsFillAvailability
      .imageFetchEnabled;
  const pipelineEnabled =
    availability.data?.operator.productSemanticsFillAvailability
      .pipelineEnabled;
  const tenantOptions =
    tenants.data?.operator.tenantSummaries.edges.map(
      (edge) => edge.node.tenant,
    ) ?? [];

  const [fills, refetchFills] = useQuery({
    query: OperatorProductSemanticsFillsDocument,
    variables: { tenantId, first: PAGE_SIZE },
    // 会社を選ぶまでは引かない（会社の指定が空のまま問い合わせない）
    pause: tenantId === "",
  });

  const [startState, startFill] = useMutation(
    OperatorStartProductSemanticsFillDocument,
  );
  const [pipelineState, startPipeline] = useMutation(
    OperatorStartProductSemanticsPipelineDocument,
  );

  const rows = fills.data?.operator.productSemanticsFills ?? [];
  const busy = startState.fetching || pipelineState.fetching;

  async function onStart(): Promise<void> {
    setNotice(null);
    // **送る前に断る。** 条件はサーバーと同じなので、ここで弾けるものを
    // 往復させない(判断の正本はサーバー側のままである)
    const parsed = parseProductIds(productIdsText, limit);
    if (parsed.error !== null) {
      setNotice(parsed.error);
      return;
    }
    const result = await startFill({
      input: {
        tenantId,
        limit,
        // 空なら送らない。従来どおり、ジョブ側の既定の選び方に任せる
        ...(parsed.ids.length > 0 ? { productIds: [...parsed.ids] } : {}),
      },
    });
    const errors = result.data?.operator.startProductSemanticsFill.userErrors;
    if (result.error !== undefined) {
      setNotice("起動できませんでした。しばらく待ってからお試しください。");
      return;
    }
    if (errors !== undefined && errors.length > 0) {
      setNotice(errors.map((error) => error.message).join(" "));
      return;
    }
    const startedId = result.data?.operator.startProductSemanticsFill.fill?.id;
    setNotice(
      startedId === undefined || startedId === null
        ? "起動しました。"
        : `起動しました（起動 ID: ${startedId}）。`,
    );
    refetchFills({ requestPolicy: "network-only" });
  }

  async function onStartPipeline(fillId: string): Promise<void> {
    setNotice(null);
    const result = await startPipeline({ input: { fillId } });
    const errors =
      result.data?.operator.startProductSemanticsPipeline.userErrors;
    if (result.error !== undefined) {
      setNotice("起動できませんでした。しばらく待ってからお試しください。");
      return;
    }
    if (errors !== undefined && errors.length > 0) {
      setNotice(errors.map((error) => error.message).join(" "));
      return;
    }
    refetchFills({ requestPolicy: "network-only" });
  }

  return (
    <div className="vd-page">
      <PageHeader
        title="属性の機械生成"
        description="商品画像から属性を作り直します。会社と件数を選んで起動します。"
        titleExtra={<HelpIcon slug="semantics-fill/run" />}
      />
      <div className="vd-stack">
        <Card title="起動する" extra={<HelpIcon slug="semantics-fill/run" />}>
          <div className="vd-row">
            <label>
              <span style={{ marginRight: 6 }}>会社</span>
              <select
                aria-label="会社"
                value={tenantId}
                onChange={(event) => {
                  setTenantId(event.target.value);
                  setNotice(null);
                }}
              >
                <option value="">会社を選んでください</option>
                {tenantOptions.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span style={{ marginRight: 6 }}>件数</span>
              <input
                aria-label="件数"
                type="number"
                min={LIMIT_MIN}
                max={LIMIT_MAX}
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                }}
              />
            </label>
            <Button
              variant="primary"
              disabled={tenantId === "" || busy || imageFetchEnabled === false}
              onClick={() => {
                void onStart();
              }}
            >
              商品画像の取り込みを起動
            </Button>
          </div>
          {/*
            固定の束は**任意**である。空なら従来どおり、ジョブ側の既定の
            選び方(レビュー件数の多い順)に任せる。
          */}
          <label
            className="vd-stack"
            style={{ display: "block", marginTop: 8 }}
          >
            <span>商品 ID（任意・改行かカンマ区切り）</span>
            <textarea
              aria-label="商品 ID（任意・改行かカンマ区切り）"
              rows={4}
              style={{ width: "100%" }}
              value={productIdsText}
              placeholder={"1100000011900\n1100000012800"}
              onChange={(event) => {
                setProductIdsText(event.target.value);
                setNotice(null);
              }}
            />
          </label>
          <p className="vd-stat-note">
            1 回に取り込めるのは {LIMIT_MAX} 件までです。商品 ID を入れると、
            その商品だけを取り込みます（空なら従来どおりの選び方です）。
            件数は上の「件数」と同じ数までにしてください。
          </p>
          {/* 押せない理由を書く。無効なボタンだけを置かない */}
          {imageFetchEnabled === false ? (
            <p className="vd-stat-note">{IMAGE_FETCH_UNCONFIGURED_REASON}</p>
          ) : tenantId === "" ? (
            <p className="vd-stat-note">会社を選ぶと起動できます。</p>
          ) : null}
          {notice === null ? null : (
            <p className="vd-stat-note" role="alert">
              {notice}
            </p>
          )}
        </Card>

        {tenantId === "" ? (
          <EmptyState
            title="会社を選んでください"
            description="選んだ会社の起動の履歴がここに出ます。"
          />
        ) : fills.fetching ? (
          <LoadingState label="起動の履歴を読み込んでいます" />
        ) : fills.error !== undefined ? (
          <ErrorState description="起動の履歴を読み込めませんでした。" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="まだ起動していません"
            description="この会社ではまだ属性の機械生成を起こしていません。"
          />
        ) : (
          rows.map((fill) => {
            const blocked = pipelineBlockedReason(fill, pipelineEnabled);
            return (
              <Card
                key={fill.id}
                title={formatJstDateTime(fill.startedAt)}
                extra={
                  <Tag
                    tone={
                      fill.state === "SUCCEEDED"
                        ? // 終わり方は正常でも、1 枚も取り込めていなければ
                          // 緑にしない（札の色だけで「うまくいった」と読める）
                          blocked === NO_IMAGES_REASON
                          ? "warn"
                          : "ok"
                        : fill.state === "FAILED"
                          ? "danger"
                          : "neutral"
                    }
                  >
                    {stateLabel(fill.state)}
                  </Tag>
                }
              >
                <div className="vd-grid">
                  <div>
                    <div className="vd-stat-label">段</div>
                    <div className="vd-stat-value">
                      {stageLabel(fill.stage)}
                    </div>
                  </div>
                  <div>
                    <div className="vd-stat-label">起動した件数</div>
                    <div className="vd-stat-value">
                      {fill.requestedLimit} 件
                    </div>
                  </div>
                  <div>
                    <div className="vd-stat-label">件数の結果</div>
                    <div className="vd-stat-value">
                      {countsLabel(fill.counts, fill.state)}
                    </div>
                  </div>
                  <div>
                    <div className="vd-stat-label">起こした人</div>
                    <div className="vd-stat-value">{fill.startedByLabel}</div>
                  </div>
                  <div>
                    <div className="vd-stat-label">終わった時刻</div>
                    <div className="vd-stat-value">
                      {fill.finishedAt == null
                        ? "まだ終わっていません"
                        : formatJstDateTime(fill.finishedAt)}
                    </div>
                  </div>
                </div>
                {fill.counts === null || fill.counts.failed === 0 ? null : (
                  <p className="vd-stat-note">
                    {fill.counts.failureReasons.length === 0
                      ? // 件数は読めたが理由の並びが読めなかった。
                        // 分かったふりをせず、そのまま書く
                        `失敗 ${fill.counts.failed} 件の理由は読み取れません。`
                      : `失敗の理由: ${fill.counts.failureReasons
                          .map(failureReasonLabel)
                          .join(" / ")}`}
                  </p>
                )}
                {fill.counts === null &&
                fill.state !== "RUNNING" &&
                fill.stage !== "SEMANTICS_PIPELINE" ? (
                  <p className="vd-stat-note">
                    ジョブは終わりましたが、取り込めた件数は読めません。
                  </p>
                ) : null}
                <div className="vd-row" style={{ marginTop: 12 }}>
                  <Button
                    disabled={blocked !== null || busy}
                    onClick={() => {
                      void onStartPipeline(fill.id);
                    }}
                  >
                    パイプラインを起動
                  </Button>
                </div>
                {blocked === null ? null : (
                  <p className="vd-stat-note">{blocked}</p>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
